<template>
    <div v-if="cartError" class="fs-3 mt-5 container-medium m-auto">
        <header class="mb-5 px-5 py-0">
            <div class="round-64 relative dinlineb valign-middle primary-50-bkg">
                <i class="fab fa-sellsy absolute-center white" style="font-size: 26px"></i>
            </div>
            <div class="dinlineb valign-middle ml-3">
                <h2 class="m-0 mb-2 dinlineb">{{ $l10n.__('Abonnement Sellsy') }}</h2>
            </div>
        </header>
        <div class="container-small m-auto cart-error-container">
            <p class="cart-error-txt" v-if="isCartLoaded && savedCartStatus == 'canceled'">
                {{ $l10n.__('Ce panier a été annulé') }}
            </p>
            <p class="cart-error-txt" v-else-if="isCartLoaded && savedCartStatus == 'paid'">
                {{ $l10n.__('Ce panier a été validé et payé') }}
            </p>
            <p class="cart-error-txt" v-else-if="!userIsSellsy && isCartLoaded && !canEditCart">
                {{
                    $l10n.__(
                        'Un panier validé est en attente de paiement, veuillez le régler avant de pouvoir éditer un nouveau panier ou contacter notre service commercial',
                    )
                }}
            </p>
            <p
                class="cart-error-txt"
                v-else-if="!userIsSellsy && hasScheduledSubscription && !canEditCart"
            >
                {{
                    $l10n.__(
                        "L'activation de votre abonnement est programmée ou en cours de finalisation",
                    )
                }}
            </p>
            <p
                class="cart-error-txt"
                v-else-if="userIsSellsy && hasScheduledSubscription && !canEditCart"
            >
                {{
                    $l10n.__(
                        'Le client a validé son abonnement, mais celui-ci est programmé ou en cours de finalisation',
                    )
                }}
            </p>
            <p
                class="cart-error-txt"
                v-else-if="
                    userIsSellsy && isCartLoaded && !canEditCart && savedCartStatus == 'validating'
                "
            >
                {{
                    $l10n.__(
                        "Le client a validé son panier, mais pas son moyen de paiement, il est nécessaire qu'il soit validé pour les modifications apportées par le panier soient prises en compte sur son abonnement",
                    )
                }}
            </p>
            <p class="cart-error-txt" v-else-if="userIsSellsy && isCartLoaded && !canEditCart">
                {{
                    $l10n.__(
                        "Le client a un panier validé en attente de paiement, il est nécessaire qu'il soit payé avant de pouvoir éditer un nouveau panier ou il faut annuler",
                    )
                }}
            </p>
            <p class="cart-error-txt" v-else>
                {{ $l10n.__('La modification d’abonnement client sera très vite disponible.') }}
            </p>
            <p class="cart-error-btn acenter">
                <CompassButton
                    v-if="!userIsSellsy"
                    :label="$t.__('common.actions.contact_sales')"
                    @click="contactCommercialUrl"
                />
                <CompassButton
                    v-if="!userIsSellsy"
                    :label="$t.__('sellsyaccountmanager.SubscriptionForm.back_to_sellsy')"
                    class="ml-3"
                    @click="homeUrl"
                />
            </p>
        </div>
    </div>
    <div v-else class="fs-3 mt-5 container-medium m-auto" v-loading="loading">
        <!-- LOADER -->
        <loader-full-page :display="loadingInit" />

        <!-- HEADER -->
        <header class="mb-5 px-5 py-0">
            <div class="round-64 relative dinlineb valign-middle primary-50-bkg">
                <i class="fab fa-sellsy absolute-center white" style="font-size: 26px"></i>
            </div>
            <div class="dinlineb valign-middle ml-3">
                <h2 class="m-0 mb-2 dinlineb">{{ $l10n.__('Abonnement Sellsy') }}</h2>

                <div v-if="!userIsSellsy" class="dinlineb ml-3">
                    <span
                        v-for="(breadcrumb, index) in stepsList"
                        :key="breadcrumb.id"
                        :class="breadcrumb === activeStep ? 'primary-50' : ''"
                    >
                        {{ index ? ' / ' + breadcrumb.label : breadcrumb.label }}
                    </span>
                </div>
                <span v-if="!userIsSellsy && activeStep.id !== 'finalize'">
                    <CompassBackForwardButton
                        v-if="stepHasPreviousOne"
                        :label="$t.__('common.actions.back')"
                        link="javascript:;"
                        back
                        class="subscriptionform-back"
                        @click="previousStep()"
                    />
                    <CompassBackForwardButton
                        v-if="!stepHasPreviousOne"
                        :label="$t.__('common.actions.back')"
                        link="javascript:history.back();"
                        back
                        class="subscriptionform-back"
                    />
                </span>
                <span v-else-if="userIsSellsy">
                    <CompassBackForwardButton
                        :label="$t.__('common.actions.back')"
                        :link="urlToCustomerThirdOverview"
                        back
                        class="subscriptionform-back"
                    />
                </span>
            </div>
        </header>

        <!-- CONTENT -->
        <div v-if="!userIsSellsy && !loadingInit" class="container-small m-auto">
            <keep-alive>
                <subscription-step-subscription
                    v-if="step('subscription') == activeStep"
                    :context="context"
                    v-bind:mode="cart ? 'view' : 'edit'"
                    @load="load"
                />
            </keep-alive>

            <subscription-step-cguv
                :type="'saas'"
                v-if="step('cguv') == activeStep"
                :actual-terms="terms"
                @load="load"
            />

            <subscription-step-finalize v-if="step('finalize') == activeStep" />
        </div>

        <div v-else-if="userIsSellsy && !loadingInit" class="m-5">
            <subscription-sale v-if="!loading" v-bind:permissions="permissions" @load="load" />
        </div>
        <!-- /CONTENT -->
    </div>
</template>
<script>
import { goToUrl } from 'vueDir/helpers.js';
// Step components
import SubscriptionStepSubscription from './subscriptionform/StepSubscription.vue';
import SubscriptionStepCguv from './subscriptionform/StepCguv.vue';
import SubscriptionStepFinalize from './subscriptionform/StepFinalize.vue';
import LoaderFullPage from 'vcomponents/utilities/LoaderFullPage.vue';

// Mixins
import SAMMixins from '../../mixins/subscription/SAMMixins';

import SubscriptionSale from './subscriptionform/SubscriptionSale.vue';

// NEW
import WidgetStore from '../../mixins/sellsyaccountmanager/store/widgetStoreMixins';
import WidgetMixins from '../../mixins/sellsyaccountmanager/widgetMixins';

export default {
    components: {
        LoaderFullPage,
        SubscriptionStepSubscription,
        SubscriptionStepCguv,
        SubscriptionStepFinalize,
        SubscriptionSale,
    },
    mixins: [SAMMixins, WidgetStore, WidgetMixins],

    props: {
        corporation: {
            type: String,
            required: true,
        },
        defaultContext: {
            type: String,
            default: 'new',
        },
        defaultCart: {
            type: Object | Boolean,
            default: false,
        },
        defaultCartStatus: {
            type: String,
        },
        defaultCartMetadata: {
            type: Object,
        },
        availableContexts: {
            type: Array,
        },
        sellsy: {
            type: Boolean,
            default: false,
        },
        permissions: {
            type: Object,
        },
        actualTerms: {
            type: Object | Boolean,
            default: false,
        },
    },

    data() {
        return {
            loadingInit: true,
            loading: false,
            customMailData: {},
            breadcrumbs: [
                {
                    id: 'subscription',
                    label: this.$l10n.__('Votre abonnement'),
                },
                {
                    id: 'cguv',
                    label: this.$l10n.__('CGVU'),
                },
                {
                    id: 'finalize',
                    label: this.$l10n.__('Validation'),
                },
            ],
            terms: false,
        };
    },

    computed: {
        hasScheduledSubscription() {
            return (
                this.billingSettings.stripeSubscriptionSchedule &&
                !this.billingSettings.stripeSubscription
            );
        },
        canEditCart() {
            if (
                this.isCartLoaded &&
                this.cartContext === this.context &&
                ['validating', 'validated'].includes(this.savedCartStatus)
            )
                return false;

            switch (this.context) {
                case 'new':
                case 'legacy_renew':
                    if (this.billingSettings.stripeSubscriptionSchedule) return false;
                    break;
                case 'upsale':
                case 'renew':
                    if (this.customer.levelReadable != 'gold') return false;
                    if (
                        !this.billingSettings.stripeSubscriptionSchedule ||
                        !this.billingSettings.stripeSubscription
                    )
                        return false;
                    break;
            }

            return true;
        },
        cartError() {
            if (!this.availableContexts.includes(this.context)) return true;
            if (this.isCartLoaded && ['validating', 'validated'].includes(this.savedCartStatus))
                return true;
            if (this.hasScheduledSubscription) return true;
            return false;
        },
    },

    created() {
        this.context = this.defaultContext;

        this.setCart(this.defaultCart);
        this.setCartStatus(this.defaultCartStatus);
        this.setCartMetadata(this.defaultCartMetadata);

        if (this.defaultContext == 'upsale') {
            this.terms = this.actualTerms;
        }

        if (this.cartError) return;

        Promise.all([
            this.getCountries(),
            this.getBillingLocales(),
            this.getCustomerDatas(this.corporation),
            this.setCustomerCorporation(this.corporation),
            this.setUserAsSellsy(this.sellsy),
            this.setUserPermissions(this.permissions),
        ])
            .then(([countries, billingLocales, customer, versions, dispatch1, dispatch2]) => {
                return Promise.all([
                    this.setCountries(countries),
                    this.setBillingLocales(billingLocales),
                    this.setCustomer(customer.data),
                    this.setQuotas(this.cartMetadata.quotas),
                    this.setPricingPlans(this.cartMetadata.plans),
                    this.setPricingProducts(this.cartMetadata.products),
                    this.setBillingSettings(customer.billingSettings),
                    this.setBillingAddress(customer.billingAddress),
                    this.setCustomerSubscribedProducts(customer.subscribedProducts),
                    this.setCartContext(this.context),
                ]);
            })
            .then(() => this.feedForm())
            .then(() =>
                this.setSavedCartTotal(
                    this.buildTotal(this.savedCartItemsIterable, this.cartPricing.plan),
                ),
            )
            .then(() => this.initSteps())
            .finally(() => (this.loadingInit = false));

        // Add listener on event
        EventBus.on('savecart', ({ callback, asDraft }) => this.processCartSave(callback, asDraft));
        EventBus.on('load', (show) => this.load(show));
        EventBus.on('cancelcart', (callback) => this.processCartCancellation(callback));
        EventBus.on('changecartContext', (context) => {
            this.loadingInit = true;
            this.context = context;
            this.setCartContext(context);

            return Promise.all([this.getActiveCartByContext(this.corporation, context)])
                .then(([cartDatas]) => {
                    this.setCart(cartDatas.cart);
                    this.setCartId(cartDatas.cart ? cartDatas.cart.id : null);
                    this.setCartStatus(cartDatas.cartStatus);
                    this.setCartMetadata(cartDatas.cartMetadata);
                })
                .then(() => this.feedForm())
                .then(() =>
                    this.setSavedCartTotal(
                        this.buildTotal(this.savedCartItemsIterable, this.cartPricing.plan),
                    ),
                )
                .finally(() => (this.loadingInit = false));
        });

        EventBus.on('saveCustomMail', (payload) => this.saveCustomMail(payload));
        EventBus.on('avoidTerms', () => {
            this.terms = false;
        });
    },

    destroyed() {
        EventBus.off('savecart');
        EventBus.off('load');
        EventBus.off('cancelcart');
        EventBus.off('changecartContext');
        EventBus.off('saveCustomMail');
        EventBus.off('avoidTerms');
    },

    methods: {
        feedForm() {
            switch (true) {
                case !!this.cart:
                    return this.feedFormWithCart();
                    break;
                case this.billingSettings.stripeSubscription:
                    return this.feedFormWithCustomer();
                    break;
                default:
                    return this.feedFormWithDefault();
                    break;
            }
        },

        load(load) {
            this.loading = load;
        },
        step(id) {
            return this.stepsList.find((step) => step.id === id);
        },
        initSteps() {
            return this.$store
                .dispatch(`defineSteps`, this.breadcrumbs)
                .then(() => this.$store.dispatch(`defineActiveStep`, this.breadcrumbs[0]));
        },
        processCartSave(callback, asDraft) {
            this.load(true);

            this.recordCart(this.customerCorporation, this.submitPayload, !!asDraft)
                .then((response) => {
                    if (response.cart) this.setCartId(response.cart.id);
                    return response.cart;
                })
                .then((cart) => {
                    // If action made by customer, relocate to public checkout
                    if (!this.userIsSellsy) Utils.relocate(this.urlToCartPublicCheckout);
                    // rewrite current url for include cart id
                    else if (this.userIsSellsy && !!asDraft)
                        this.$router.push(
                            this.urlCartSaleWithCart,
                            () => {},
                            () => {},
                        );

                    return cart;
                })
                .then((cart) => {
                    if (typeof callback === 'function') {
                        callback(cart);
                        this.load(false);
                    }
                })
                .catch((e) => {
                    switch (e.error.message) {
                        case 'terms':
                            Push.error(
                                this.$l10n.__(
                                    "La dernière version des Conditions Générales d'Utilisation et de Ventes n'est pas acceptée",
                                ),
                            );
                            this.goToStep('cguv');
                            break;
                        case 'creditcard':
                            Push.error(this.$l10n.__('Vos informations bancaires sont requises'));
                            this.goToStep('subscription');
                            break;
                        case 'context':
                            Push.error(
                                this.$l10n.__(
                                    "La modification de votre abonnement n'est pas encore disponible",
                                ),
                            );
                            break;
                        case 'cart_saving_forbidden':
                            Push.error(
                                this.$l10n.__(
                                    'La modification de votre abonnement est actuellement indisponible',
                                ),
                            );
                            this.goToStep('subscription');
                            break;
                        case 'cart_saving':
                            Push.error(
                                this.$l10n.__("Une erreur est survenue lors de l'enregistrement"),
                            );
                            this.goToStep('subscription');
                            break;
                        default:
                            Push.error(
                                this.$l10n.__(
                                    "Une erreur est survenue lors de la création de l'abonnement",
                                ),
                            );
                            break;
                    }
                    this.load(false);
                });
        },
        processCartCancellation(callback) {
            // cart exists
            if (!this.isCartLoaded) {
                Push.error(this.$l10n.__('Aucun panier à annuler'));
                return;
            }

            // loader
            this.load(true);

            // process
            this.cancelCart(this.cartId)
                .then(() => this.setCartId(null))
                .then(() => {
                    if (typeof callback === 'function') callback();
                })
                .catch((e) => {
                    switch (e.error.message) {
                        case 'cart_not_found':
                            Push.error(this.$l10n.__('Le panier est introuvable'));
                            break;
                        case 'cart_forbidden':
                            Push.error(
                                this.$l10n.__("Vous n'avez pas le droit d'annuler ce panier"),
                            );
                            break;
                        case 'cart_already_canceled':
                            Push.error(this.$l10n.__('Le panier est déjà annulé'));
                            break;
                        default:
                            Push.error(
                                this.$l10n.__(
                                    "Une erreur est survenue lors de l'annulation du panier",
                                ),
                            );
                            break;
                    }
                    return this.load(false);
                });
        },
        setCart(cart) {
            this.cart = cart;
        },
        setCartStatus(cartStatus) {
            this.cartStatus = cartStatus;
        },
        setCartMetadata(cartMetadata) {
            this.cartMetadata = cartMetadata;
        },
        setCustomMail(customMail) {
            this.customMailData = { ...customMail };
        },
        contactCommercialUrl() {
            return goToUrl('javascript:MySupport.getForm();');
        },
        homeUrl() {
            return goToUrl('/home');
        },
    },
};
</script>

<style scoped>
.subscriptionform-back {
    display: flex;
    justify-content: flex-start;
}
.cart-error-container {
    margin-top: 64px !important;
}
.cart-error-txt {
    color: #111111;
    font-size: 24px;
    font-weight: bold;
    letter-spacing: 0.5px;
    line-height: 29px;
    text-align: center;
}
.cart-error-btn {
    margin-top: 64px !important;
}
</style>
